import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverPortallessContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { HubspotCityOption } from "@/constants/hubspot-ticket-cities";
import { normalize } from "@/features/vision-360/proposal-standard/hubspotCity";

interface TicketCityFieldProps {
  value: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: HubspotCityOption[];
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

/** Accent/case-insensitive: "sao paulo" finds "São Paulo - SP". The keywords
 * carry the label, which is what the rep reads (it differs from the value on
 * a couple of HubSpot options). */
function filterCity(value: string, search: string, keywords?: string[]) {
  const wanted = normalize(search);
  if (!wanted) return 1;
  return [value, ...(keywords ?? [])].some((text) =>
    normalize(text).includes(wanted),
  )
    ? 1
    : 0;
}

/**
 * The ticket's city as a pick-from-list field: free text let typos reach the
 * Zap and break the ticket (PED-14049). Only HubSpot's options are offered,
 * and the form value is always the option `value`.
 */
export function TicketCityField({
  value,
  onChange,
  onBlur,
  options,
  isLoading = false,
  hasError = false,
  onRetry,
}: TicketCityFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  // Without the list there is nothing valid to pick; say so instead of
  // opening an empty dropdown.
  if (hasError) {
    return (
      <div
        className="w-full h-10 px-3 rounded-xl text-sm flex items-center justify-between"
        style={{ border: "1px solid #FCA5A5", color: "#B91C1C" }}
      >
        <span className="truncate">Erro ao carregar cidades</span>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs font-semibold underline shrink-0 ml-2"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          disabled={isLoading}
          aria-expanded={open}
          aria-label="Cidade"
          className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors flex items-center justify-between text-left"
          style={{
            border: `1px solid ${open ? "#FFD900" : "#E8E6E3"}`,
            color: selected ? "#43403D" : "#9E9A94",
          }}
        >
          <span className="truncate">
            {isLoading
              ? "Carregando cidades..."
              : selected
                ? selected.label
                : "Selecione a cidade"}
          </span>
          <ChevronsUpDown className="size-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      {/* Portalless: the ticket sheet is a modal dialog, and its focus trap
          keeps focus out of a popover portaled to <body> (the search input
          would never receive what the rep types). */}
      <PopoverPortallessContent align="start" className="w-[280px] p-0">
        <Command filter={filterCity}>
          <CommandInput placeholder="Buscar cidade..." />
          <CommandList>
            <CommandEmpty>Nenhuma cidade encontrada</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                    onBlur?.();
                  }}
                >
                  {option.label}
                  <Check
                    className="ml-auto size-4"
                    style={{ opacity: option.value === value ? 1 : 0 }}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverPortallessContent>
    </Popover>
  );
}
